'use client'

import React from 'react'

import { HiMiniChevronDown } from 'react-icons/hi2'

import Button from '@/components/common/Button'
import Checkbox from '@/components/common/Checkbox'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/common/DropdownMenu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/common/Select'
import { FormTextArea } from '@/components/common/TextAreaV2'
import { FormInput } from '@/components/common/TextInputV2'
import {
  CurrencyInput,
  DateInput,
  NumberInput,
} from '@/components/library-v2/catalog/ui/Field/Edit'

import type { SalesforceField } from '@/hooks/salesforce/types'

export interface SalesforceFieldInputProps {
  field: SalesforceField
  value: string | number | boolean | null | undefined
  onChange: (v: string | number | boolean | null) => void
  error?: string
  disabled?: boolean
}

export const SalesforceFieldInput: React.FC<SalesforceFieldInputProps> =
  React.memo(({ field, value, onChange, error, disabled = false }) => {
    switch (field.type) {
      case 'string':
      case 'phone':
      case 'email':
      case 'url':
      case 'id':
        return (
          <FormInput
            value={(value ?? '') as string}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange(e.target.value)
            }
            type={
              field.type === 'email'
                ? 'email'
                : field.type === 'url'
                  ? 'url'
                  : 'text'
            }
            error={error}
            disabled={disabled}
            placeholder={`Enter ${field.label}`}
          />
        )
      case 'textarea':
        return (
          <FormTextArea
            value={(value ?? '') as string}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              onChange(e.target.value)
            }
            rows={4}
            error={error}
            disabled={disabled}
            placeholder={`Enter ${field.label}`}
          />
        )
      case 'picklist':
        return (
          <Select
            value={(value ?? '') as string}
            onValueChange={onChange}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue placeholder={`Select ${field.label}`} />
            </SelectTrigger>
            <SelectContent>
              {field.picklistValues
                ?.filter((opt) => opt.active)
                .sort((a, b) => a.label.localeCompare(b.label))
                .map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        )
      case 'multipicklist': {
        const options =
          field.picklistValues
            ?.filter((opt) => opt.active)
            .sort((a, b) => a.label.localeCompare(b.label)) ?? []
        const selected: string[] =
          typeof value === 'string' && value.length > 0
            ? (value as string).split(';').filter(Boolean)
            : []
        const selectedSet = new Set(selected)

        const toggle = (optValue: string, checked: boolean) => {
          const next = new Set(selectedSet)
          if (checked) {
            next.add(optValue)
          } else {
            next.delete(optValue)
          }
          const serialized = Array.from(next).join(';')
          onChange(serialized)
        }

        const labelText =
          selected.length > 0
            ? `${selected.length} selected`
            : `Select ${field.label}`

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline-compact"
                className="active:scale-100 flex items-center justify-between h-9 w-full font-normal px-3 rounded-md border-primary-200 hover:border-primary-300 dark:border-primary-800 dark:hover:border-primary-700"
                disabled={disabled}
              >
                <span
                  className={
                    selected.length === 0
                      ? 'text-primary-500 dark:text-primary-400'
                      : 'text-primary-900 dark:text-primary-100'
                  }
                >
                  {labelText}
                </span>
                <HiMiniChevronDown className="flex-shrink-0 h-4 w-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              portal={true}
              className="min-w-[300px] max-h-64 overflow-y-auto"
              align="start"
            >
              {options.map((opt) => {
                const isChecked = selectedSet.has(opt.value)
                return (
                  <DropdownMenuCheckboxItem
                    key={opt.value}
                    checked={isChecked}
                    onCheckedChange={(c: boolean) => toggle(opt.value, c)}
                    className="h-8"
                  >
                    {opt.label}
                  </DropdownMenuCheckboxItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )
      }
      case 'boolean':
        return (
          <div className="flex items-center space-x-2">
            <Checkbox
              id={field.name}
              checked={(value ?? false) as boolean}
              onClick={() => onChange(!((value ?? false) as boolean))}
              disabled={disabled}
            />
          </div>
        )
      case 'date':
        return (
          <DateInput
            label={undefined}
            required={!field.nillable}
            format="date"
            value={(value as string) ?? ''}
            onChange={(v) => onChange(v || null)}
            error={error}
          />
        )
      case 'datetime':
        return (
          <DateInput
            label={undefined}
            required={!field.nillable}
            format="datetime"
            value={(value as string) ?? ''}
            onChange={(v) => onChange(v || null)}
            error={error}
          />
        )
      case 'int':
      case 'double':
      case 'percent':
        return (
          <NumberInput
            label={undefined}
            required={!field.nillable}
            value={
              typeof value === 'number'
                ? (value as number)
                : value === '' || value === null || value === undefined
                  ? null
                  : Number(value)
            }
            onChange={(n) => onChange(n as number | null)}
            error={error}
          />
        )
      case 'currency':
        return (
          <CurrencyInput
            label={undefined}
            required={!field.nillable}
            value={
              typeof value === 'number'
                ? (value as number)
                : value === '' || value === null || value === undefined
                  ? null
                  : Number(value)
            }
            onChange={(n) => onChange(n as number | null)}
            error={error}
          />
        )
      case 'reference':
        return null
      default:
        return (
          <FormInput
            value={(value ?? '') as string}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange(e.target.value)
            }
            error={error}
            disabled={disabled}
            placeholder={`Enter ${field.label}...`}
          />
        )
    }
  })

SalesforceFieldInput.displayName = 'Salesforce.FieldInput'
